/*
 * predictive-search.js — search cluster
 *
 * Defines two custom elements that used to be supplied by the deleted remote
 * vendor bundle (the vendor runtime):
 *
 *   <search-form>        base class SearchForm
 *   <predictive-search>  class PredictiveSearch extends SearchForm
 *
 * Both are re-implementations of Shopify's open-source Dawn theme (MIT):
 * Dawn `assets/global.js`  -> SearchForm
 * Dawn `assets/predictive-search.js` -> PredictiveSearch
 * adapted to the DOM this theme actually renders.
 *
 * LOAD-ORDER CONTRACT (do not break):
 *   `SearchForm` MUST be a top-level `class` declaration in a CLASSIC (non-module)
 *   script. `assets/main-search.js:1` reads it as a bare identifier
 *   (`class MainSearch extends SearchForm {`) and that file is injected in <body>
 *   by `sections/main-search.liquid:8`. Therefore this file has to be a deferred
 *   classic <script> in <head> (the slot vacated by the vendor tag at
 *   layout/theme.liquid:29-36) so it executes first.
 *   Wrapping any of this in an IIFE, or shipping it as type="module", produces a
 *   ReferenceError in main-search.js.
 *
 * Selectors used here were all read out of this theme's Liquid — see the comment
 * above each one for file + line.
 *
 * ES2019 only: no optional chaining, no nullish coalescing, no imports.
 */

/* ------------------------------------------------------------------------- *
 * Local helpers.
 *
 * `debounce`, `trapFocus` and `removeTrapFocus` are USED by assets/secondary.js
 * but DEFINED nowhere in the theme — they came from the deleted vendor bundle.
 * This file must not claim ownership of the global `debounce` name (another
 * cluster owns the shared-globals file), so it consumes the global when one
 * exists and otherwise falls back to a private, identically-behaving copy under
 * a clearly non-colliding name.
 * ------------------------------------------------------------------------- */

/** Private stand-in for the missing global `debounce`. Same semantics as Dawn's. */
function themeSearchDebounceFallback(fn, wait) {
  var timer;
  return function () {
    var context = this;
    var args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, wait);
  };
}

/** Prefer the shared global `debounce` if some other file has already defined it. */
function themeSearchDebounce(fn, wait) {
  try {
    // `typeof` on an undeclared identifier is safe; a TDZ binding would throw,
    // hence the try/catch.
    if (typeof debounce === 'function') return debounce(fn, wait);
  } catch (error) {
    /* fall through to the local copy */
  }
  return themeSearchDebounceFallback(fn, wait);
}

/**
 * Wrap an event handler so a failure inside the search UI can never bubble out
 * and break unrelated page JS (add-to-cart, cart drawer, etc.).
 */
function themeSearchSafeHandler(fn, context) {
  return function (event) {
    try {
      return fn.call(context, event);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[predictive-search]', error);
    }
  };
}

/* ------------------------------------------------------------------------- *
 * SearchForm
 *
 * Rendered as <search-form> only when settings.predictive_search_enabled is off:
 *   sections/header.liquid:365  <search-form class="search-modal__form">
 *   sections/header.liquid:688  <search-form class="search-modal__form" data-main='false'>
 * It must exist as a global class on every page regardless, because
 * assets/main-search.js:1 extends it and sections/main-search.liquid renders
 * <main-search> (main-search.liquid:72) on the search template.
 * ------------------------------------------------------------------------- */
class SearchForm extends HTMLElement {
  constructor() {
    super();

    // header.liquid:369-387 (#Search-In-Modal-1), header.liquid:692-710
    // (#Search-In-Modal), main-search.liquid:75-94 (#Search-In-Template)
    this.input = this.querySelector('input[type="search"]');
    // header.liquid:390, header.liquid:713, main-search.liquid:110
    // class="reset__button field__button{% if search.terms == blank %} hidden{% endif %}"
    this.resetButton = this.querySelector('button[type="reset"]');

    // Assigned BEFORE this bail-out on purpose: assets/main-search.js:11
    // dereferences `this.input` from the subclass without a guard.
    if (!this.input) return;

    // The wrapping <form>: header.liquid:367, header.liquid:690, main-search.liquid:73
    if (this.input.form) {
      this.input.form.addEventListener('reset', themeSearchSafeHandler(this.onFormReset, this));
    }

    var self = this;
    this.input.addEventListener(
      'input',
      themeSearchDebounce(function (event) {
        try {
          self.onChange(event);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[predictive-search]', error);
        }
      }, 300)
    );
  }

  /**
   * Show/hide the reset ("clear") button. `hidden` is defined in
   * assets/base.css: `.hidden, .no-js-inline, ... {display: none !important;}`
   * and is the only class this cluster ever toggles. Its initial state comes
   * from the Liquid (`{% if search.terms == blank %} hidden{% endif %}`).
   */
  toggleResetButton() {
    if (!this.resetButton || !this.input) return;

    var resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  // Prototype method: PredictiveSearch overrides it and calls super.onChange().
  onChange() {
    this.toggleResetButton();
  }

  /**
   * Document-wide on purpose (Dawn behaviour): a click on a predictive-search
   * option in ANY instance must suppress the form reset so the link wins.
   * The only markup carrying aria-selected is sections/predictive-search.liquid
   * (:22, :31, :49, :58, :88, :118, :127 — each <li role="option"> holds an <a>).
   * Prototype method: assets/main-search.js:23 calls super.shouldResetForm().
   */
  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  /**
   * Prototype method: assets/main-search.js:22 calls super.onFormReset(event).
   * preventDefault() first so the native reset cannot restore the input's
   * Liquid-rendered `value="{{ search.terms | escape }}"`.
   */
  onFormReset(event) {
    if (event) event.preventDefault();
    if (!this.input) return;

    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

/* ------------------------------------------------------------------------- *
 * PredictiveSearch
 *
 * Rendered at:
 *   sections/header.liquid:363       (logo top-center / no menu modal)
 *   sections/header.liquid:686       (header icons modal, data-main='false')
 *   sections/main-search.liquid:70   (search template, data-main='true', wraps <main-search>)
 *
 * Result markup comes from sections/predictive-search.liquid via the Section
 * Rendering API; it is never rendered with {% section %}.
 * ------------------------------------------------------------------------- */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    // header.liquid:403, header.liquid:726 (.predictive-search--header),
    // main-search.liquid:99 (.predictive-search--search-template)
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    // Read as a PLAIN PROPERTY by sections/header.liquid:848 inside
    // StickyHeader.onScroll: `if (this.predictiveSearch && this.predictiveSearch.isOpen) return;`
    // It must never become a method.
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    if (!this.input) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input.form) {
      this.input.form.addEventListener('submit', themeSearchSafeHandler(this.onFormSubmit, this));
    }

    // Fires when DetailsModal (assets/secondary.js) focus-traps into the input
    // on modal open.
    this.input.addEventListener('focus', themeSearchSafeHandler(this.onFocus, this));
    this.addEventListener('focusout', themeSearchSafeHandler(this.onFocusOut, this));
    this.addEventListener('keyup', themeSearchSafeHandler(this.onKeyup, this));
    this.addEventListener('keydown', themeSearchSafeHandler(this.onKeydown, this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    super.onChange();

    var newSearchTerm = this.getQuery();

    // Drop stale results when the new term does not extend the old one, so they
    // cannot flash back when the dropdown reopens.
    // #predictive-search-results-groups-wrapper: predictive-search.liquid:10
    if (!this.searchTerm || newSearchTerm.indexOf(this.searchTerm) !== 0) {
      var groupsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (groupsWrapper) groupsWrapper.remove();
    }

    // Update the "Search for X" label optimistically, before the fetch returns.
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  /**
   * Let a keyboard-selected option's link win over submitting the form, and
   * never submit an empty query.
   */
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    var currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // The term was changed from another search input (MainSearch.keepInSync,
      // assets/main-search.js:40) — treat it as a user change.
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    var self = this;
    // Deferred a tick so document.activeElement has settled on the new target.
    setTimeout(function () {
      if (!self.contains(document.activeElement)) self.close();
    });
  }

  /**
   * NOTE: no stopPropagation() here — Escape must keep bubbling to DetailsModal
   * in assets/secondary.js, which closes the search modal on keyup.
   */
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      default:
        break;
    }
  }

  onKeydown(event) {
    // Stop the caret from jumping to the start/end of the input while the user
    // is walking the option list.
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  /**
   * Rewrite the "Search for <terms>" label in place.
   * [data-predictive-search-search-for-text]: predictive-search.liquid:163
   */
  updateSearchForTerm(previousTerm, newTerm) {
    var searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;

    var currentButtonText = searchForTextElement.innerText;
    if (!currentButtonText) return;

    try {
      // A term the shopper typed can contain regex metacharacters ("(", "[", ...),
      // which would make `new RegExp(previousTerm)` throw. Bail out quietly and
      // leave the label for the fetched markup to correct.
      var matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        // Ambiguous replacement — leave it alone.
        return;
      }
    } catch (error) {
      return;
    }

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  /**
   * Move the aria-selected marker up/down the visible option list.
   *
   * The `element.offsetParent !== null` filter is REQUIRED: predictive-search.liquid
   * renders the pages/articles list twice (desktop :43-67, mobile :112-136) and
   * assets/component-predictive-search.css display:none's whichever one is not in
   * play at the current breakpoint.
   */
  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    var moveUp = direction === 'up';
    // li.predictive-search__list-item[role="option"] (predictive-search.liquid:22 etc.)
    // and button.predictive-search__item--term (predictive-search.liquid:157-165)
    var selectedElement = this.querySelector('[aria-selected="true"]');
    var allVisibleElements = Array.prototype.slice
      .call(this.querySelectorAll('li, button.predictive-search__item'))
      .filter(function (element) {
        return element.offsetParent !== null;
      });

    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    var selectedElementIndex = allVisibleElements.indexOf(selectedElement);

    if (this.statusElement) this.statusElement.textContent = '';

    var activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    var activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    // NOTE: the "search for" option's id sits on the WRAPPING div
    // (#predictive-search-option-search-keywords, predictive-search.liquid:156)
    // while role="option" sits on the inner <button> (:157), which has no id.
    // Landing on it therefore writes an empty aria-activedescendant. That is
    // Dawn's plain behaviour; left as-is deliberately (see followUps).
    if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    // <a> inside a selected <li> (predictive-search.liquid:23 etc.) or the
    // selected "search for" <button> (predictive-search.liquid:157).
    var selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  /**
   * Section Rendering API fetch against the store's own origin only.
   * routes.predictive_search_url comes from window.routes, layout/theme.liquid:678.
   */
  getSearchResults(searchTerm) {
    var self = this;
    var queryKey = searchTerm.replace(' ', '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    var predictiveSearchUrl =
      window.routes && window.routes.predictive_search_url ? window.routes.predictive_search_url : '';

    if (!predictiveSearchUrl) {
      // Never hardcode /search/suggest — without window.routes there is nothing
      // safe to call, so fail closed rather than guessing.
      // eslint-disable-next-line no-console
      console.warn('[predictive-search] window.routes.predictive_search_url is missing.');
      this.close();
      return;
    }

    fetch(
      predictiveSearchUrl +
        '?q=' +
        encodeURIComponent(searchTerm) +
        '&section_id=predictive-search',
      { signal: this.abortController.signal }
    )
      .then(function (response) {
        if (!response.ok) {
          var error = new Error(response.status);
          self.close();
          throw error;
        }
        return response.text();
      })
      .then(function (text) {
        // The Section Rendering API wraps sections/predictive-search.liquid in
        // #shopify-section-predictive-search.
        var section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) {
          self.close();
          return;
        }

        var resultsMarkup = section.innerHTML;

        // Keep the cache synced across every <predictive-search> on the page
        // (up to three: header.liquid:363, header.liquid:686, main-search.liquid:70)
        // so switching between them costs no extra bandwidth.
        Array.prototype.forEach.call(self.allPredictiveSearchInstances, function (instance) {
          if (instance && instance.cachedResults) instance.cachedResults[queryKey] = resultsMarkup;
        });

        self.renderSearchResults(resultsMarkup);
      })
      .catch(function (error) {
        // DOMException code 20 == ABORT_ERR: a newer keystroke superseded this one.
        if (error && (error.code === 20 || error.name === 'AbortError')) return;
        self.close();
        // eslint-disable-next-line no-console
        console.error('[predictive-search]', error);
      });
  }

  /**
   * .predictive-search-status: header.liquid:411, header.liquid:734,
   * main-search.liquid:107. data-loading-text: header.liquid:363/:686,
   * main-search.liquid:70.
   */
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    // Attribute-driven, not class-driven: component-predictive-search.css:113-138.
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    var statusElement = this.statusElement;
    statusElement.setAttribute('aria-hidden', 'false');
    statusElement.textContent = statusText;

    setTimeout(function () {
      statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    // [data-predictive-search-live-region-count-value]: predictive-search.liquid:169
    var countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countValue ? countValue.textContent : '');
  }

  /**
   * .section-header is the header section wrapper class declared in the
   * header.liquid schema ("class": "section-header", header.liquid:969).
   */
  getResultsMaxHeight() {
    var sectionHeader = document.querySelector('.section-header');
    var headerBottom = sectionHeader ? sectionHeader.getBoundingClientRect().bottom : 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      // Dawn writes the cached value back without a unit on re-open, which the
      // CSSOM silently drops; always emitting `px` yields the same pixel value
      // and valid CSS.
      var maxHeight = this.resultsMaxHeight ? this.resultsMaxHeight : this.getResultsMaxHeight();
      this.predictiveSearchResults.style.maxHeight = maxHeight + 'px';
    }
    // component-predictive-search.css:63-66 keys the dropdown off [open].
    this.setAttribute('open', true);
    if (this.input) this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm) {
    this.closeResults(clearSearchTerm === true);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm) {
    if (clearSearchTerm === true) {
      if (this.input) this.input.value = '';
      this.removeAttribute('results');
    }

    var selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', false);
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
  }
}

/* ------------------------------------------------------------------------- *
 * Registration.
 * `search-form` must be registered before assets/main-search.js runs
 * customElements.define('main-search', MainSearch) at main-search.js:49 —
 * guaranteed by the <head>-deferred load order described at the top of this file.
 * Do NOT define `main-search` here; it already exists in assets/main-search.js.
 * ------------------------------------------------------------------------- */
if (!customElements.get('search-form')) {
  customElements.define('search-form', SearchForm);
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

// Belt-and-braces: the top-level `class` declarations above already create the
// global lexical bindings that assets/main-search.js:1 relies on. These extra
// window properties cost nothing and help any later script that looks the
// classes up dynamically.
window.SearchForm = window.SearchForm || SearchForm;
window.PredictiveSearch = window.PredictiveSearch || PredictiveSearch;
